import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider } from "@/context/AuthContext";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import WhatsAppButton from "@/components/layout/WhatsAppButton";
import HomePage from "@/pages/HomePage";
import GeneratePage from "@/pages/GeneratePage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import PricingPage from "@/pages/PricingPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import CommunityPage from "@/pages/CommunityPage";
import SheLeadsPage from "@/pages/SheLeadsPage";
import BlogPage from "@/pages/BlogPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import DashboardPage from "@/pages/DashboardPage";
import SettingsPage from "@/pages/SettingsPage";
import AuthCallback from "@/pages/AuthCallback";
import AdminPage from "@/pages/AdminPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import CookiePage from "@/pages/CookiePage";
import CareersPage from "@/pages/CareersPage";
import ApiPage from "@/pages/ApiPage";
import BlogArticlePage from "@/pages/BlogArticlePage";
import InsightsPage from "@/pages/InsightsPage";
import DemoModeBanner from "@/components/DemoModeBanner";
import PublicAssessmentPage from "@/pages/PublicAssessmentPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AssessmentsPage from "@/pages/AssessmentsPage";
import AxisAssessmentPage from "@/pages/AxisAssessmentPage";
import TreeAnalysisPage from "@/pages/TreeAnalysisPage";
import CapScanLandingPage from "@/pages/CapScanLandingPage";
import CapScanSetupPage from "@/pages/CapScanSetupPage";
import CapScanRatingPage from "@/pages/CapScanRatingPage";
import CapScanReportPage from "@/pages/CapScanReportPage";
import MovementReportPage from "@/pages/MovementReportPage";
import CoachingLandingPage from "@/pages/CoachingLandingPage";
import CoachingSetupPage from "@/pages/CoachingSetupPage";
import CoachingSessionPage from "@/pages/CoachingSessionPage";
import CoachingDashboardPage from "@/pages/CoachingDashboardPage";
import ProtectedRoute from "@/components/ProtectedRoute";
import SharedMovementReportPage from "@/pages/SharedMovementReportPage";
import TrustPage from "@/pages/TrustPage";
import { installAuthInterceptor } from "@/lib/authInterceptor";

// Install once at module load — silently refreshes the access token on any 401
installAuthInterceptor();

// Canonicalize host: www.roleiq.pro → roleiq.pro so cookies always work
if (typeof window !== "undefined" && window.location.hostname === "www.roleiq.pro") {
  window.location.replace(`https://roleiq.pro${window.location.pathname}${window.location.search}${window.location.hash}`);
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppLayout({ children, hideNav, hideFooter, hideWhatsApp }) {
  return (
    <>
      {!hideNav && <Navbar />}
      <DemoModeBanner />
      <main>{children}</main>
      {!hideFooter && <Footer />}
      {!hideWhatsApp && <WhatsAppButton />}
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  // Check URL fragment for session_id from Google OAuth — must happen synchronously before routes
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<AppLayout><HomePage /></AppLayout>} />
      <Route path="/generate" element={<AppLayout hideFooter><GeneratePage /></AppLayout>} />
      <Route path="/how-it-works" element={<AppLayout><HowItWorksPage /></AppLayout>} />
      <Route path="/pricing" element={<AppLayout><PricingPage /></AppLayout>} />
      <Route path="/about" element={<AppLayout><AboutPage /></AppLayout>} />
      <Route path="/contact" element={<AppLayout><ContactPage /></AppLayout>} />
      <Route path="/community" element={<AppLayout><CommunityPage /></AppLayout>} />
      <Route path="/she-leads" element={<AppLayout><SheLeadsPage /></AppLayout>} />
      <Route path="/blog" element={<AppLayout><BlogPage /></AppLayout>} />
      <Route path="/insights" element={<AppLayout><InsightsPage /></AppLayout>} />
      <Route path="/assess/:token" element={<PublicAssessmentPage />} />
      <Route path="/login" element={<AppLayout hideFooter><LoginPage /></AppLayout>} />
      <Route path="/signup" element={<AppLayout hideFooter><SignupPage /></AppLayout>} />
      <Route path="/dashboard" element={<ProtectedRoute><AppLayout hideFooter hideWhatsApp><DashboardPage /></AppLayout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><AppLayout hideFooter hideWhatsApp><SettingsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AppLayout hideFooter hideWhatsApp><AdminPage /></AppLayout></ProtectedRoute>} />
      <Route path="/privacy" element={<AppLayout><PrivacyPage /></AppLayout>} />
      <Route path="/terms" element={<AppLayout><TermsPage /></AppLayout>} />
      <Route path="/cookies" element={<AppLayout><CookiePage /></AppLayout>} />
      <Route path="/careers" element={<AppLayout><CareersPage /></AppLayout>} />
      <Route path="/developers" element={<AppLayout><ApiPage /></AppLayout>} />
      <Route path="/blog/:slug" element={<AppLayout><BlogArticlePage /></AppLayout>} />
      <Route path="/reset-password" element={<AppLayout hideFooter><ResetPasswordPage /></AppLayout>} />
      <Route path="/assessments" element={<AppLayout><AssessmentsPage /></AppLayout>} />
      <Route path="/assessments/axis" element={<AppLayout hideFooter><AxisAssessmentPage /></AppLayout>} />
      <Route path="/assessments/tree" element={<AppLayout hideFooter><TreeAnalysisPage /></AppLayout>} />
      <Route path="/capscan" element={<AppLayout><CapScanLandingPage /></AppLayout>} />
      <Route path="/capscan/new" element={<ProtectedRoute><AppLayout hideFooter hideWhatsApp><CapScanSetupPage /></AppLayout></ProtectedRoute>} />
      <Route path="/capscan/rate/:scan_id" element={<ProtectedRoute><AppLayout hideFooter hideWhatsApp><CapScanRatingPage /></AppLayout></ProtectedRoute>} />
      <Route path="/capscan/report/:report_id" element={<ProtectedRoute><AppLayout hideFooter hideWhatsApp><CapScanReportPage /></AppLayout></ProtectedRoute>} />
      <Route path="/capscan/movement-report/:report_id" element={<ProtectedRoute><AppLayout hideFooter hideWhatsApp><MovementReportPage /></AppLayout></ProtectedRoute>} />
      <Route path="/m/:token" element={<AppLayout hideFooter hideWhatsApp><SharedMovementReportPage /></AppLayout>} />
      <Route path="/trust" element={<AppLayout><TrustPage /></AppLayout>} />
      <Route path="/coaching" element={<AppLayout><CoachingLandingPage /></AppLayout>} />
      <Route path="/coaching/start" element={<ProtectedRoute><AppLayout hideFooter hideWhatsApp><CoachingSetupPage /></AppLayout></ProtectedRoute>} />
      <Route path="/coaching/session/:session_id" element={<ProtectedRoute><AppLayout hideFooter hideWhatsApp><CoachingSessionPage /></AppLayout></ProtectedRoute>} />
      <Route path="/coaching/dashboard" element={<ProtectedRoute><AppLayout hideFooter hideWhatsApp><CoachingDashboardPage /></AppLayout></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
